import { useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { getRoleCode } from "constant/Role"
import { Wallet, Landmark, PackageSearch } from "lucide-react"
import { Page, Button } from "./ui"
import { KpiTile, KpiStrip, EmptyState } from "procurement/design"
import StatusBadge from "./components/StatusBadge"
import ProcurementFilterBar from "./components/ProcurementFilterBar"
import ScopedDataTable from "./components/ScopedDataTable"
import useProcurementFilters from "./hooks/useProcurementFilters"
import { useGetPurchasesQuery, useGetPurchaseSuppliersQuery } from "shared/redux/endpoints/procurementPurchases"
import { useGetActiveBudgetCategoriesQuery } from "shared/redux/endpoints/procurementV2"
import { useGetProcurementCentersQuery } from "shared/redux/endpoints/procurementItems"
import { formatVND, formatDate } from "./constants"

const FILTER_DEFAULTS = { search: "", region: "", center: "", category: "", supplier: "" }

// F1 — To Pay List. Every Purchase that's payable, still has a balance left
// to pay, and doesn't already have an active Payment Request on it (GET
// /purchases?payable=true&excludeWithActivePayment=true — `remainingToPay`
// comes straight off each row). Clicking a row opens the Purchase Payment
// Context (F2).
//
// `standalone`: same pattern as PurchasesContainer — used when mounted under
// the normal (non-manager) Procurement tab rather than its own sidebar item.
// Reached by picking a PO here to pre-link a new Payment Request to it (F2);
// the "Create Payment" button on the Payment list instead opens the
// contextless create form, which offers this same PO-linked path alongside
// "Without a Purchase Order" and "Direct spend — no PO".
const ToPayListContainer = ({ standalone = false, title, breadcrumb }) => {
  const navigate = useNavigate()
  const { filters, setFilters, resetFilters } = useProcurementFilters(FILTER_DEFAULTS)
  const activeRoleCode = useSelector((state) => getRoleCode(state.auth?.role))

  // NOTE: `payable: true` routes GET /purchases through a different BE query
  // path (see procurementPurchases.js's getPurchases) that reads region/
  // category/supplier/q and `center` (a PO matches when at least one source OR
  // belongs to that center) — not department/item/from_date/to_date.
  const { data, isFetching } = useGetPurchasesQuery({
    payable: true,
    // Hides POs that already have a non-rejected Payment Request linked —
    // this list is for *starting* a new payment, so a PO already on one
    // shouldn't be pickable again. Server-side (SQL WHERE NOT EXISTS), safe
    // to combine with `payable` (different filter: remaining balance vs.
    // an active payment link).
    excludeWithActivePayment: true,
    region: filters.region || undefined,
    category: filters.category || undefined,
    supplier: filters.supplier || undefined,
    center: filters.center || undefined,
    role: activeRoleCode,
    size: 100,
  })
  const { data: centersPage } = useGetProcurementCentersQuery({
    region: filters.region === "North" || filters.region === "South" ? filters.region : undefined,
    size: 200,
  })
  const centerOptions = useMemo(
    () =>
      (centersPage?.items || []).map((c) => ({
        value: String(c.centerId),
        label: c.sunCenterCode ? `${c.centerName} (${c.sunCenterCode})` : c.centerName,
      })),
    [centersPage]
  )
  const { data: categories } = useGetActiveBudgetCategoriesQuery({ isActive: true })
  const { data: suppliers } = useGetPurchaseSuppliersQuery()

  const categoryOptions = useMemo(
    () => (categories || []).map((c) => ({ value: String(c.id), label: c.name })),
    [categories]
  )
  const supplierOptions = useMemo(
    () => (suppliers || []).map((s) => ({ value: String(s.id), label: s.name })),
    [suppliers]
  )

  const rows = useMemo(() => {
    const items = (data?.items || [])
      .map((p) => {
        const id = p.id ?? p.poCode
        const total = p.totalAmount ?? p.total
        return {
          id,
          poCode: p.poCode,
          created: p.createdAt,
          region: p.region,
          center: p.center || p.centerName,
          category: p.budgetCategoryName || p.categoryName,
          supplier: p.vendorName || p.supplierName,
          total,
          remaining: p.remainingToPay,
          status: p.status,
          reason: p.reason,
        }
      })
      .filter((r) => r.remaining > 0)
    const q = filters.search.trim().toLowerCase()
    return q ? items.filter((r) => `${r.poCode} ${r.supplier}`.toLowerCase().includes(q)) : items
  }, [data, filters.search])

  const totalOutstanding = rows.reduce((sum, r) => sum + r.remaining, 0)

  const columns = useMemo(
    () => [
      { Header: "PO Code", accessor: "poCode" },
      { Header: "Issued", accessor: "created", Cell: ({ value }) => formatDate(value) },
      { Header: "Region", accessor: "region" },
      { Header: "Category", accessor: "category" },
      { Header: "Supplier", accessor: "supplier" },
      { Header: "Total", accessor: "total", align: "right", Cell: ({ value }) => formatVND(value) },
      {
        Header: "Remaining to pay",
        accessor: "remaining",
        align: "right",
        Cell: ({ value }) => (
          <span className="pc-money-due">{formatVND(value)}</span>
        ),
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value, row }) => (
          <span className="pc-stack">
            <StatusBadge status={value} />
            {row.original.reason && <span className="pc-cell-sub pc-cell-reason">{row.original.reason}</span>}
          </span>
        ),
      },
      {
        Header: "",
        accessor: "id",
        Cell: ({ value }) => (
          <Button size="sm" variant="secondary" onClick={() => navigate(`/procurement/to-pay/${value}`)}>
            View
          </Button>
        ),
      },
    ],
    [navigate]
  )

  const heading = standalone ? title || "To Pay" : "To Pay"

  return (
    <Page
      className="pc-page--refined pc-page--list"
      breadcrumb={standalone ? [breadcrumb || "Procurement", heading] : ["Procurement", heading]}
      title={heading}
      subtitle={
        standalone
          ? undefined
          : "Purchase orders that have been issued and delivered but still carry a balance — start a Payment Request from any of them."
      }
    >
      <KpiStrip>
        <KpiTile
          icon={<Wallet aria-hidden />}
          iconTone="warning"
          label="POs awaiting payment"
          value={isFetching ? "—" : rows.length}
        />
        <KpiTile
          icon={<Landmark aria-hidden />}
          iconTone="danger"
          label="Total outstanding"
          value={isFetching ? "—" : formatVND(totalOutstanding)}
        />
      </KpiStrip>

      <ProcurementFilterBar
        fields={["search", "region", "center", "category", "supplier"]}
        categoryOptions={categoryOptions}
        supplierOptions={supplierOptions}
        centerOptions={centerOptions}
        values={filters}
        onChange={setFilters}
        onReset={resetFilters}
      />

      {!isFetching && rows.length === 0 ? (
        <div className="ds-table-shell">
          <EmptyState
            icon={<PackageSearch aria-hidden />}
            title="Nothing left to pay"
            text="Every issued purchase order is either fully paid or hasn't been delivered yet. Try clearing the filters."
            action={
              <Button variant="secondary" size="sm" onClick={resetFilters}>
                Clear filters
              </Button>
            }
          />
        </div>
      ) : (
        <>
          <div className="ds-table-toolbar is-attached">
            <span>Purchases to pay</span>
            <span className="pc-toolbar-count">
              {isFetching ? "Refreshing…" : `${rows.length} PO${rows.length === 1 ? "" : "s"}`}
            </span>
          </div>
          <div className="ds-table-shell">
            <ScopedDataTable columns={columns} rows={rows} isLoading={isFetching} emptyText="No purchases found." />
          </div>
        </>
      )}
    </Page>
  )
}

export default ToPayListContainer
